import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import VilkarKort from "../components/VilkarKort";
import { useSideTittel } from "../components/tilgjengelighet";
import { getDisplayName } from "../utils/displayName";
import { kundeFerdigLinje } from "../utils/ferdigDatoVisning";
import { useSmsKode } from "../utils/smsKode";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const maskerTelefon = (t) => {
  const d = String(t || "").replace(/\D/g, "");
  return d ? d.slice(0, -3).replace(/./g, "*") + d.slice(-3) : "";
};

// Rett telefonnummeret på bookingen (kunden skrev feil). Koden sendes til det nye
// nummeret; bookingen kobles til riktig kundekort (POST /bookings/public/:token/telefon).
function RettTelefon({ token, telefon, onRettet }) {
  const [apen, setApen] = useState(false);
  const [nytt, setNytt] = useState("");
  const [kode, setKode] = useState("");
  const [sendtTil, setSendtTil] = useState("");
  const [opptatt, setOpptatt] = useState(false);
  const [feil, setFeil] = useState("");
  const [status, setStatus] = useState("");
  const feltRef = useRef(null);
  const kodeRef = useRef(null);
  const bekreftRef = useRef(null);
  const knappRef = useRef(null);

  useEffect(() => { if (apen && !sendtTil) feltRef.current?.focus(); }, [apen, sendtTil]);
  useEffect(() => { if (sendtTil) kodeRef.current?.focus(); }, [sendtTil]);
  useSmsKode(!!sendtTil, (k) => {
    setKode(k);
    setTimeout(() => bekreftRef.current?.focus(), 0);
  });

  const kallApi = async (sti, body) => {
    const r = await fetch(`${API_BASE_URL}/bookings/public/${token}/${sti}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.message || "Noe gikk galt");
    return data;
  };
  const lukk = (melding = "") => {
    setApen(false);
    setSendtTil("");
    setNytt("");
    setKode("");
    setFeil("");
    setStatus(melding);
    setTimeout(() => knappRef.current?.focus(), 0);
  };
  const sendKode = async (e) => {
    e.preventDefault();
    setFeil("");
    setOpptatt(true);
    try {
      const d = await kallApi("telefon", { telefon: nytt });
      setSendtTil(d.masked || nytt);
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };
  const bekreft = async (e) => {
    e.preventDefault();
    setFeil("");
    setOpptatt(true);
    try {
      await kallApi("telefon/bekreft", { kode });
      onRettet(nytt);
      lukk("Telefonnummeret er rettet. Du får en SMS med bekreftelsen.");
    } catch (err) {
      setFeil(err.message);
    } finally {
      setOpptatt(false);
    }
  };

  const felt = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm";
  return (
    <div className="mt-3 pt-3 border-t">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium">Mobilnummer på bookingen:</span> {maskerTelefon(telefon) || "Ikke registrert"}
        </div>
        {!apen && (
          <button ref={knappRef} type="button" onClick={() => { setStatus(""); setApen(true); }}
            className="shrink-0 px-3 py-1.5 rounded border text-sm hover:bg-gray-50">
            Rett telefonnummer
          </button>
        )}
      </div>
      {apen && !sendtTil && (
        <form onSubmit={sendKode} className="mt-3 space-y-2">
          <label htmlFor="rett-telefon" className="block text-sm font-medium text-gray-800">Riktig mobilnummer</label>
          <input ref={feltRef} id="rett-telefon" type="tel" inputMode="tel" autoComplete="tel" value={nytt}
            onChange={(e) => setNytt(e.target.value)} className={felt} required maxLength={20}
            aria-invalid={feil ? true : undefined}
            aria-describedby={`rett-telefon-hjelp${feil ? " rett-telefon-feil" : ""}`} />
          <p id="rett-telefon-hjelp" className="text-xs text-gray-700">
            Vi sender en kode dit. Når du har bekreftet den, får du meldingene om bookingen på det nummeret.
          </p>
          {feil && <p id="rett-telefon-feil" className="text-sm text-red-700" role="alert">{feil}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={opptatt || !nytt.trim()} className="px-4 py-2 rounded bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium disabled:opacity-50">
              {opptatt ? "Sender…" : "Send kode"}
            </button>
            <button type="button" onClick={() => lukk()} className="px-3 py-2 rounded border text-sm hover:bg-gray-50">Avbryt</button>
          </div>
        </form>
      )}
      {apen && sendtTil && (
        <form onSubmit={bekreft} className="mt-3 space-y-2">
          <label htmlFor="rett-telefon-kode" className="block text-sm font-medium text-gray-800">Kode</label>
          <input ref={kodeRef} id="rett-telefon-kode" type="text" inputMode="numeric" autoComplete="one-time-code"
            maxLength={6} value={kode} onChange={(e) => setKode(e.target.value.replace(/\D/g, ""))}
            className={`${felt} font-mono tracking-[0.3em]`} required
            aria-invalid={feil ? true : undefined}
            aria-describedby={`rett-telefon-kode-hjelp${feil ? " rett-telefon-feil" : ""}`} />
          <p id="rett-telefon-kode-hjelp" className="text-xs text-gray-700" role="status">
            Vi har sendt en 6-sifret kode til {sendtTil}. Den er gyldig i 10 minutter.
          </p>
          {feil && <p id="rett-telefon-feil" className="text-sm text-red-700" role="alert">{feil}</p>}
          <div className="flex flex-wrap gap-2">
            <button ref={bekreftRef} type="submit" disabled={opptatt || kode.length !== 6} className="px-4 py-2 rounded bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium disabled:opacity-50">
              {opptatt ? "Sjekker…" : "Bekreft"}
            </button>
            <button type="button" onClick={() => lukk()} className="px-3 py-2 rounded border text-sm hover:bg-gray-50">Avbryt</button>
          </div>
        </form>
      )}
      <p className="text-sm text-gray-800 mt-2" role="status">{status}</p>
    </div>
  );
}

const fmtDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("no-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value).slice(0, 10)
    : d.toLocaleDateString("no-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

export default function PublicBookingPortal() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [vilkar, setVilkar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [closed, setClosed] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendError, setSendError] = useState("");

  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelMsg, setCancelMsg] = useState("");

  // Bildeopplasting
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const imageInputRef = useRef(null);

  useSideTittel(`Din booking – ${getDisplayName()}`);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      setErr("");

      const resp = await fetch(`${API_BASE_URL}/bookings/public/${token}`, {
        cache: "no-store",
      });

      const body = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(body?.message || "Fant ikke booking");
      }

      if (body.redirectToService) {
        navigate(`/svc/${body.redirectToService}`, { replace: true });
        return;
      }

      if (body.closed) {
        setClosed(true);
        return;
      }

      setBooking(body.booking || null);
      setVilkar(body.vilkar || null);
    } catch (e) {
      console.error("Feil ved lasting av bookingportal:", e);
      setErr("Lenken er ugyldig, deaktivert eller utløpt.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const messages = useMemo(() => {
    const raw = Array.isArray(booking?.messages) ? booking.messages : [];
    return [...raw].sort((a, b) => {
      const da = new Date(a.createdAt || 0).getTime();
      const db = new Date(b.createdAt || 0).getTime();
      return db - da;
    });
  }, [booking]);

  const isBookingOpen = String(booking?.status || "").toLowerCase() === "booking";

  const postCustomerMessage = async (text) => {
    const cleanText = String(text || "").trim();
    if (!cleanText) return false;

    const resp = await fetch(`${API_BASE_URL}/bookings/public/${token}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text: cleanText,
        fromName: "Kunde",
      }),
    });

    const body = await resp.json().catch(() => ({}));

    if (!resp.ok) {
      throw new Error(body?.message || "Kunne ikke sende melding");
    }

    setBooking((prev) => ({
      ...prev,
      messages: Array.isArray(body.messages) ? body.messages : prev?.messages || [],
    }));

    return true;
  };

  const sendMessage = async () => {
    const text = String(messageText || "").trim();
    if (!text) return;

    try {
      setSendingMessage(true);
      setSendError("");

      await postCustomerMessage(text);
      setMessageText("");
    } catch (e) {
      console.error("Feil ved sending av melding:", e);
      setSendError(e.message || "Kunne ikke sende melding.");
    } finally {
      setSendingMessage(false);
    }
  };

  const requestCancellation = async () => {
    if (!isBookingOpen) return;

    const confirmed = window.confirm(
      "Vil du sende melding til verkstedet om at du ønsker å kansellere bookingen?"
    );
    if (!confirmed) return;

    try {
      setCancelBusy(true);
      setCancelMsg("");
      setSendError("");

      await postCustomerMessage(
        "Hei! Jeg ønsker å kansellere denne bookingen. Kan dere bekrefte dette?"
      );

      setCancelMsg("Meldingen om kansellering er sendt til verkstedet.");
    } catch (e) {
      console.error("Feil ved kanselleringsønske:", e);
      setCancelMsg(e.message || "Kunne ikke sende kanselleringsmelding.");
    } finally {
      setCancelBusy(false);
    }
  };

  const handleImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImagePreviews((prev) => [
      ...prev,
      ...files.map((file) => ({ file, objectUrl: URL.createObjectURL(file) })),
    ]);
    setImageUploadError("");
    e.target.value = "";
  }, []);

  const removeImagePreview = useCallback((idx) => {
    setImagePreviews((prev) => {
      URL.revokeObjectURL(prev[idx].objectUrl);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const uploadImages = useCallback(async () => {
    if (!imagePreviews.length || uploadingImages) return;
    setUploadingImages(true);
    setImageUploadError("");
    try {
      const form = new FormData();
      imagePreviews.forEach(({ file }) => form.append("images", file));

      const resp = await fetch(`${API_BASE_URL}/bookings/public/${token}/images`, {
        method: "POST",
        body: form,
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body?.message || "Opplasting feilet");

      setBooking((prev) => ({
        ...prev,
        images: Array.isArray(body.images) ? body.images : prev?.images || [],
        messages: Array.isArray(body.messages) ? body.messages : prev?.messages || [],
      }));

      imagePreviews.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
      setImagePreviews([]);
    } catch (e) {
      setImageUploadError(e?.message || "Kunne ikke laste opp bilder. Prøv igjen.");
    } finally {
      setUploadingImages(false);
    }
  }, [imagePreviews, uploadingImages, token]);

  if (loading) {
    return <div className="p-6" role="status">Laster booking…</div>;
  }

  if (err) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-1">Din booking</h1>
        <p className="text-red-700" role="alert">{err}</p>
      </div>
    );
  }

  if (closed) {
    return (
      <div className="mx-auto w-full max-w-xl p-8 text-center">
        <div className="text-4xl mb-4" aria-hidden="true">🔒</div>
        <h1 className="text-xl font-semibold mb-2">Bookingen er avsluttet</h1>
        <p className="text-gray-600 text-sm">
          Denne bookingportalen er ikke lenger tilgjengelig. Ta kontakt med verkstedet dersom du har spørsmål.
        </p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-1">Din booking</h1>
        <p>Fant ikke booking.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl p-4 md:p-6">
      <h1 className="text-2xl font-semibold mb-1">Din booking</h1>
      <p className="text-sm text-gray-600 mb-6">
        Opprettet {fmtDateTime(booking.createdAt)}
      </p>

      <VilkarKort
        vilkar={vilkar}
      />

      {/* Kontaktinfo endres på Din side, ikke via lenken (kundeflyt fase 3) */}
      <div className="border rounded-lg p-4 mb-4 text-sm">
        <h2 className="font-semibold mb-2 text-lg">Kontaktinformasjon</h2>
        <p className="text-gray-800">
          Vil du endre e-post eller mobilnummer fast? Det gjør du på{" "}
          <a href="/min-side" className="text-blue-700 underline font-medium">Din side</a>.
        </p>
        {isBookingOpen && (
          <RettTelefon
            token={token}
            telefon={booking?.customer?.phoneNumber}
            onRettet={(tlf) =>
              setBooking((prev) => (prev ? { ...prev, customer: { ...(prev.customer || {}), phoneNumber: tlf } } : prev))
            }
          />
        )}
      </div>

      <div className="border rounded-lg p-4 mb-4 bg-blue-50">
        <h2 className="font-semibold mb-2 text-lg">Status</h2>
        <div className="text-sm">
          Nåværende status: <span className="font-semibold">{booking.status || "Ukjent"}</span>
        </div>
      </div>

      <div className="border rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="font-semibold text-lg">Produkter</h2>

          <button
            type="button"
            onClick={requestCancellation}
            disabled={!isBookingOpen || cancelBusy}
            className={`px-4 py-2 rounded text-sm text-white ${
              !isBookingOpen || cancelBusy
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {cancelBusy ? "Sender…" : "Ønsker å kansellere booking"}
          </button>
        </div>

        {cancelMsg ? (
          <div className="mb-3 text-sm text-gray-800" role="status">{cancelMsg}</div>
        ) : null}

        {Array.isArray(booking.items) && booking.items.length ? (
          <div className="space-y-5">
            {booking.items.map((prod, idx) => (
              <div key={idx} className="border-b pb-4 last:border-b-0 last:pb-0">
                <div className="font-medium text-base">
                  {prod.produktmerke || prod.produktnavn || "Produkt"}
                  {prod.farge ? ` (${prod.farge})` : ""}
                  {prod.produkttype ? ` – ${prod.produkttype}` : ""}
                </div>

                <div className="mt-2 space-y-1 text-sm text-gray-700">
                  {prod.leveringsDato && (
                    <div>Leveringsdato: {fmtDate(prod.leveringsDato)}</div>
                  )}
                  {(() => {
                    const ferdig = kundeFerdigLinje(prod, fmtDate, "Forventet ferdig innen");
                    return ferdig ? <div>{ferdig.label}: {ferdig.tekst}</div> : null;
                  })()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-600">Ingen produkter registrert.</div>
        )}
      </div>

      <div className="border rounded-lg p-4 mb-4">
        <h2 className="font-semibold mb-3 text-lg">Dialog med verksted</h2>

        {messages.length ? (
          <ul className="space-y-2 mb-4">
            {messages.map((msg, idx) => {
              const fromCustomer = msg.from === "customer";

              return (
                <li
                  key={idx}
                  className={`flex ${fromCustomer ? "justify-end" : "justify-start"}`}
                >
                  <div className="max-w-[80%]">
                    <div className="text-xs text-gray-500 mb-1">
                      {fromCustomer ? "Deg" : msg.fromName || "Verksted"} ·{" "}
                      {fmtDateTime(msg.createdAt)}
                    </div>

                    <div
                      className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                        fromCustomer
                          ? "bg-emerald-700 text-white"
                          : "bg-gray-100 text-gray-900"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-600 mb-4">
            Ingen meldinger ennå.
          </p>
        )}

        {isBookingOpen ? (
          <div className="space-y-2">
            <label htmlFor="booking-melding" className="block text-xs font-medium text-gray-700">
              Din melding til verkstedet
            </label>
            <textarea
              id="booking-melding"
              className="w-full border rounded px-3 py-2 text-sm min-h-[90px]"
              placeholder="Skriv en melding til verkstedet..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
            />

            {sendError ? <div className="text-sm text-red-700" role="alert">{sendError}</div> : null}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={sendMessage}
                disabled={sendingMessage || !messageText.trim()}
                className={`px-4 py-2 rounded text-white text-sm ${
                  sendingMessage || !messageText.trim()
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-emerald-700 hover:bg-emerald-800"
                }`}
              >
                {sendingMessage ? "Sender…" : "Send melding"}
              </button>

              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                title="Last opp bilde"
                aria-label="Last opp bilde"
                className="p-2 rounded border border-gray-300 hover:bg-gray-50 text-gray-600"
              >
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>

            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleImageSelect}
            />

            {imagePreviews.length > 0 && (
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap gap-2">
                  {imagePreviews.map(({ objectUrl }, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded overflow-hidden border bg-gray-50">
                      <img src={objectUrl} alt={`Valgt bilde ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImagePreview(idx)}
                        aria-label={`Fjern bilde ${idx + 1}`}
                        className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                {imageUploadError && (
                  <div className="text-xs text-red-700" role="alert">{imageUploadError}</div>
                )}
                <button
                  type="button"
                  onClick={uploadImages}
                  disabled={uploadingImages}
                  className={`px-4 py-2 rounded text-sm font-medium text-white ${uploadingImages ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-700 hover:bg-emerald-800"}`}
                >
                  {uploadingImages ? "Laster opp…" : `Send ${imagePreviews.length} bilde${imagePreviews.length === 1 ? "" : "r"}`}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            Chat er stengt fordi bookingen ikke lenger har status booking.
          </div>
        )}
      </div>

      {/* Kundebilder */}
      {Array.isArray(booking.images) && booking.images.length > 0 && (
        <div className="border rounded-lg p-4 mb-4">
          <h2 className="font-semibold mb-3 text-lg">
            Bilder
            <span className="ml-2 text-xs font-normal text-gray-500">
              {booking.images.length} bilde{booking.images.length !== 1 ? "r" : ""}
            </span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {booking.images.map((img, idx) => (
              <div key={img._id || idx} className="border rounded-lg overflow-hidden bg-gray-50">
                <a href={img.url} target="_blank" rel="noreferrer" className="block relative">
                  <img
                    src={img.url}
                    alt={img.originalName || "Bilde"}
                    className="w-full h-24 object-cover"
                  />
                  {img.uploadedByCustomer && (
                    <span className="absolute top-1 left-1 text-[10px] bg-emerald-700 text-white px-1.5 py-0.5 rounded">
                      Deg
                    </span>
                  )}
                </a>
                {(img.originalName || img.uploadedAt) && (
                  <div className="p-2">
                    {img.originalName && (
                      <div className="text-xs text-gray-800 break-words truncate">{img.originalName}</div>
                    )}
                    {img.uploadedAt && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {fmtDateTime(img.uploadedAt)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}


      <p className="text-xs text-gray-500">
        Denne siden er en kundelenke. Del den ikke videre.
      </p>
    </div>
  );
}